<template>
  <div
    v-if="stepData"
    class="step"
  >
    <component
      :is="question.questionComponent"
      v-for="question in questions"
      :key="question.questionId || question.questionComponent"
      product-name="travel"
      step-id="travellers"
      :question-id="question.questionId"
      v-bind="question.params || {}"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="stepData"
      step-type="single"
      @toast="showToast($event)"
      @input="updateStepData"
      @loading="$emit('loading', $event)"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { cloneDeep } from 'lodash';
import {
  inject,
  computed,
} from '@vue/composition-api';

export default {
  name: 'form-travel-step2',
  inject: ['helpers'],
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    isExperimentTravelMinimiseHelptext: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      stepData: null,
    };
  },
  watch: {
    stepData: {
      handler (val) {
        this.$emit('step-data', val);
      },
      immediate: true,
    },
  },
  created () {
    this.initStep();
    this.formObserver.reset();
    this.updateGtmTracking();
  },
  computed: {
    questions () {
      return [
        {
          questionId: 'type-of-party',
          questionComponent: 'question-base-radio-list',
          params: {
            customFields: {
              field: 'typeOfParty',
            },
            title: 'Who is the cover for?',
            helpText: {
              collapsible: this.isExperimentTravelMinimiseHelptext,
              openerText: 'Defining Travel Party',
              value: '<p>A <b>couple</b> is two adults aged 18 or over who are in a relationship and live at the same address.</p><p>A <b>family</b> is one or two adults living at the same address in addition to at least one child (up to a maximum of 9 if one adult is travelling). If two adults are travelling, they must be a spouse or partner.</p><p>A <b>group</b> is several individuals travelling together under a single insurance policy (up to a maximum of 10).</p>',
            },
            optionsConfig: {
              options: {
                AD: 'Individual Adult',
                CO: 'Couple',
                F: 'Family',
                SG: 'Group',
              },
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please confirm the party type who will require cover.',
              },
            },
          },
        },
        {
          questionId: 'people-adults-traveling',
          questionComponent: 'question-base-dropdown',
          params: {
            customFields: {
              field: 'travellingAdults',
            },
            title: 'Number of adults travelling?',
            optionsConfig: {
              options: {
                1: '1',
                2: '2',
              },
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please confirm number of adults travelling.',
              },
            },
            showRules: {
              typeOfParty: {
                isAny: {
                  other: ['F'],
                },
                required: true,
              },
            },
          },
        },
        {
          questionId: 'people-children-traveling',
          questionComponent: 'question-base-dropdown',
          params: {
            customFields: {
              field: 'travellingChildren',
            },
            title: 'Number of children travelling?',
            optionsConfig: {
              options: {
                1: '1',
                2: '2',
                3: '3',
                4: '4',
                5: '5',
                6: '6',
                7: '7',
                8: '8',
              },
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please confirm number of children travelling.',
              },
            },
            showRules: {
              typeOfParty: {
                isAny: {
                  other: ['F'],
                },
                required: true,
              },
            },
          },
        },
        {
          questionId: 'people-group-traveling',
          questionComponent: 'question-base-dropdown',
          params: {
            customFields: {
              field: 'travellingPeople',
            },
            title: 'How many people will be covered under this policy?',
            optionsConfig: {
              options: {
                2: '2',
                3: '3',
                4: '4',
                5: '5',
                6: '6',
                7: '7',
                8: '8',
                9: '9',
                10: '10',
              },
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please confirm the number of people travelling.',
              },
            },
            showRules: {
              typeOfParty: {
                isAny: {
                  other: ['SG'],
                },
                required: true,
              },
            },
          },
        },
        {
          questionId: 'date-of-birth',
          questionComponent: 'question-date-of-birth',
          params: {
            helpText: false,
          },
        },
        {
          questionId: 'couple-date-of-birth',
          questionComponent: 'question-date-of-birth',
          params: {
            customFields: {
              field: 'dateOfBirthAdult_2',
            },
            title: "What's your partner's date of birth?",
            maxYears: 105,
            minYears: 18,
            validationRules: {
              field: {
                required: true,
                dateOfBirth: true,
                minAge: 18,
                maxAge: 105,
              },
            },
            validationErrors: {
              field: {
                minAge: 'The minimum age for this type of insurance is 18.',
                maxAge: 'The second traveller age must be maximum 105 years.',
                required: 'Please enter the date.',
                dateOfBirth: 'Please ensure the date of birth is correct.',
              },
            },
            showRules: {
              typeOfParty: {
                isAny: {
                  other: ['CO'],
                },
                required: true,
              },
            },
          },
        },
        {
          questionId: 'date-of-birth-family',
          questionComponent: 'question-date-of-birth-family',
          params: {
            customFields: {
              field: 'dateOfBirthFamily',
            },
            title: "What's the date of birth?",
            maxYears: 18,
            minYears: 0,
            validationRules: {
              field: {
                childrenMaxAge: 17,
                travelRequired: true,
                childrenDateOfBirth: true,
              },
            },
            validationErrors: {
              field: {
                childrenMaxAge: 'The child must be younger than 18 years',
                travelRequired: 'Please ensure the date of birth is correct.',
                childrenDateOfBirth: 'Please choose a date that is not in the future.',
              },
            },
            isVerticalSplitted: true,
          },
        },
        {
          questionId: 'date-of-birth-group',
          questionComponent: 'question-date-of-birth-group',
          params: {
            customFields: {
              field: 'dateOfBirthGroup',
            },
            title: "What's the traveller's date of birth?",
            maxYears: 105,
            minYears: 0,
            validationRules: {
              field: {
                minAge: 0,
                maxAge: 105,
                travelRequired: true,
                travelDateOfBirth: true,
              },
            },
            validationErrors: {
              field: {
                minAge: "The traveller's age must be minimum 0 years.",
                maxAge: "The traveller's age must be maximum 105 years.",
              },
            },
            showRules: {
              typeOfParty: {
                isAny: {
                  other: ['SG'],
                },
                required: true,
              },
              travellingPeople: {
                isAny: {
                  other: ['2', '3', '4', '5', '6', '7', '8', '9', '10'],
                },
                required: true,
              },
            },
            isVerticalSplitted: true,
          },
        },
        {
          questionId: 'title',
          questionComponent: 'question-base-radio-list',
          params: {
            customFields: {
              field: 'title',
            },
            title: 'What is your title?',
            optionsConfig: {
              options: this.getTitleOptions,
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select a title.',
              },
            },
          },
        },
        {
          questionId: 'name',
          questionComponent: 'question-name',
          params: {
            title: 'What is your name?',
            customFields: [
              'firstName',
              'surname',
            ],
            prevalidationRules: {
              firstName: {
                name: [
                  2,
                  50,
                ],
                regex: '^([A-Za-z-\\s]|[À-Ö]|[Ø-ö]|[ø-ǿ]|[Ȁ-ʯ]|[̀-ͯ]|[Ḁ-ỿ]|[Ⅰ-ↈ])+$',
              },
              surname: {
                name: [
                  2,
                  50,
                ],
                regex: '^([A-Za-z-\\s]|[À-Ö]|[Ø-ö]|[ø-ǿ]|[Ȁ-ʯ]|[̀-ͯ]|[Ḁ-ỿ]|[Ⅰ-ↈ])+$',
              },
            },
            validationRules: {
              firstName: {
                name: [
                  2,
                  50,
                ],
              },
              surname: {
                name: [
                  2,
                  50,
                ],
              },
            },
          },
        },
        {
          questionId: 'email',
          questionComponent: 'question-email',
          params: {
            title: 'Email address',
            customFields: [
              'email',
            ],
            prevalidationRules: {
              email: true,
            },
            validationRules: {
              email: {
                email: true,
                required: true,
              },
            },
          },
        },
        {
          questionId: 'user-data',
          questionComponent: 'question-password',
        },
        {
          questionId: 'direct-marketing-options',
          questionComponent: 'question-direct-marketing-options',
          params: {
            customFields: {
              directMarketingEmail: 'directMarketingEmail',
            },
          },
        },
        {
          questionId: 'user-keep-sign-in',
          questionComponent: 'question-user-keep-sign-in',
          params: {
            title: 'Keep me signed in',
            customFields: {
              userKeepSignIn: 'userKeepSignIn',
            },
            defaultValues: {
              userKeepSignIn: true,
            },
          },
        },
        {
          questionId: 'terms-and-conditions',
          questionComponent: 'question-terms-and-conditions',
          params: {
            customFields: {
              termsAndConditions: 'termsAndConditions',
            },
          },
        },
      ];
    },
  },
  methods: {
    ...mapActions(['showToast']),
    initStep () {
      this.stepData = cloneDeep(this.form.travellers);
    },
    updateGtmTracking () {
      this.$emit('tracking', 'travellers');
    },
    updateStepData (questionData) {
      for (const key in questionData) {
        this.stepData[key] = questionData[key];
      }
    },
  },
  setup () {
    const {
      isMxDrLiveTravel,
    } = inject('abTastyControllerUI');

    const getTitleOptions = computed(() => {
      const titles = {
        Mr: 'Mr',
        Mrs: 'Mrs',
        Ms: 'Ms',
        Miss: 'Miss',
        MDr: 'Dr (Male)',
        FDr: 'Dr (Female)',
      };
      const newTitles = {
        Mr: 'Mr',
        Mrs: 'Mrs',
        Ms: 'Ms',
        Miss: 'Miss',
        Dr: 'Dr',
        Mx: 'Mx',
      };

      return isMxDrLiveTravel.value ? newTitles : titles;
    });

    return {
      // COMPUTED
      getTitleOptions,
    };
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .direct-marketing {
    margin-top: 23px;
  }
}
</style>
